import { Colors } from "@/enums/Colors";
import { LazoPaths } from "@/enums/LazoPaths";
import { TypeOfBusinessArray } from "@/enums/TypeOfBusiness";
import GenericForm from "@/external/ensolvers-core-frontend-mui-v5/components/Common/GenericForm";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import { RadioInput } from "@/external/ensolvers-core-frontend-mui-v5/components/FormInputs/RadioInput";
import { TextFieldInput } from "@/external/ensolvers-core-frontend-mui-v5/components/FormInputs/TextFieldInput";
import AsyncAuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AsyncAuthService";
import CustomAuthServiceV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/CustomAuthServiceV2";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { ActivityOnboarding } from "@/interfaces/onboarding-interfaces/ActivityOnboarding";
import { CompanyOnboarding } from "@/interfaces/onboarding-interfaces/CompanyOnboarding";
import { Onboarding } from "@/interfaces/onboarding-interfaces/Onboarding";
import { UserOnboarding } from "@/interfaces/onboarding-interfaces/UserOnboarding";
import LazoLeadService from "@/services/LazoLeadService";
import LazoUserService from "@/services/LazoUserService";
import OnboardingService from "@/services/OnboardingService";
import { OAuthRedirectUser } from "@/types/OAuthRedirectUser";
import { SuccessfullyOnboarding } from "@/types/SuccessfullyOnboarding";
import { yupResolver } from "@hookform/resolvers/yup";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Box,
  Button,
  Divider,
  FormControlProps,
  IconButton,
  InputAdornment,
  RadioProps as MuiRadioProps,
  RadioGroupProps,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import ReactGA from "react-ga4";
import { FieldErrors, FieldValues, Resolver } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import SignUpQuestionLogo from "../../../assets/images/sign-up-question-logo.svg";
import LazoPurpleBackground from "../utils/LazoPurpleBackground";
import { RadioButtonIcon } from "../utils/RadioButtonIcon";
import { ContinueButton } from "./ContinueButton";
import HeaderOnBoardingComponent from "./HeaderOnBoardingComponent";
import { Container, Text, textFieldStyles } from "./LazoOnboarding";
import { OboardingStepsComponent } from "./OboardingStepsComponent";
import { OnboardingCheckbox } from "./OnboardingCheckbox";
import { StyledImage } from "./OnboardingCompanyForm";
import { PreviousButton } from "./PreviousButton";
import { useActivityFormRefs } from "./activity-form-error-control/useActivityFormRefs";

const radioProps: MuiRadioProps = {
  icon: <RadioButtonIcon strokeColor={Colors.LIGHT_GRAY_DEDEE1} />,
  checkedIcon: <RadioButtonIcon strokeColor={Colors.LIGHT_GRAY_DEDEE1} checked checkedColor={Colors.LAZO_PURPLE} />,
};

const radioGroupProps: RadioGroupProps = {
  row: true,
  sx: {
    margin: 0,
  },
};

const formControlProps: FormControlProps = {
  sx: {
    padding: 0,
    fontSize: "80px",
    width: "100%",
    "@media (max-width: 600px)": {
      //alignItems: "center",
    },
    "& .MuiRadio-root": {
      padding: "0 0.5rem 0 0",
    },
    "& .MuiFormControlLabel-label": {
      fontSize: ".75rem",
    },
  },
};

const SkipForNowButton = styled(Button)({
  backgroundColor: Colors.WHITE,
  borderRadius: ".5rem",
  border: `1px solid ${Colors.LAZO_PURPLE}`,
  height: "3.3125rem",
  textTransform: "none",
});

interface OnboardingActivityFormProps {
  activityInformation: ActivityOnboarding;
  userComesFromProductPage?: boolean;
  userComesFromCheckoutPage?: boolean;
  handleContinue: (activityInformation: ActivityOnboarding, hasSkippedActivityForm: boolean) => void;
  handlePrevious: () => void;
  onSave?: (onboardingForm?: Onboarding) => void;
}

export const OnboardingActivityForm = ({ userComesFromProductPage, handleContinue, handlePrevious }: OnboardingActivityFormProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const { myRefs, propertyNames } = useActivityFormRefs();
  const [errorsRef, setErrorsRef] = useState<FieldErrors<FieldValues>>();

  const [isLoggedUser, setIsLoggedUser] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const schema = Yup.object()
    .shape({
      expenses: Yup.number().typeError("This field must be a number").min(0, "0 is the minimum value").required("This field is required"),
      statesWithEmployees: Yup.number()
        .typeError("This field must be a number")
        .min(0, "0 is the minimum value")
        .required("This field is required"),
      bookkeeper: Yup.string().required("This field is required"),
      foreignShareholdersAmount: Yup.number()
        .typeError("This field must be a number")
        .when("foreignShareholders", {
          is: true,
          then: (schema) => schema.required("This field is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
      internationalSubsidiariesAmount: Yup.number()
        .typeError("This field must be a number")
        .when("internationalSubsidiaries", {
          is: true,
          then: (schema) => schema.required("This field is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
      lastYearExpenses: Yup.number()
        .typeError("This field must be a number")
        .when("taxAssistance", {
          is: true,
          then: (schema) => schema.required("This field is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
      lastYearStatesWithEmployees: Yup.number()
        .typeError("This field must be a number")
        .when("taxAssistance", {
          is: true,
          then: (schema) => schema.required("This field is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
    })
    .required();

  useEffect(() => {
    const LoggedUserData = JSON.parse(localStorage.getItem("TenantMemberships__CurrentTenantMembership"));

    if (LoggedUserData) {
      setIsLoggedUser(true);
    } else {
      setIsLoggedUser(false);
    }

    if (errorsRef) {
      let errorPropertyName = null;

      for (const element of Object.keys(errorsRef)) {
        if (propertyNames.includes(element)) {
          errorPropertyName = element;
          break;
        }
      }
      if (errorPropertyName) {
        myRefs[errorPropertyName].current.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  }, [errorsRef]);

  const submitRef = useRef(null);
  const handleFormSubmit = () => {
    if (submitRef.current) {
      ReactGA.event({
        category: "user",
        action: "InstantQuote",
      });
      console.log("[GA]", "Instant Quote");
      submitRef.current(); // Call the submit function
    }
  };

  const registerUserAndAddToCompany = async (user: UserOnboarding) => {
    setIsLoading(true);
    try {
      const company: CompanyOnboarding = JSON.parse(sessionStorage.getItem("companyInformation"));
      if (company.otherIndustry != null) {
        company.industry = company.otherIndustry;
        company.otherIndustry = null;
      }

      const activityInformation: ActivityOnboarding = JSON.parse(sessionStorage.getItem("activityOnboarding"));
      const hasSkippedActivityForm: boolean = sessionStorage.getItem("hasSkippedActivityForm") === "true";

      const onboardingForm: Onboarding = {
        user: user,
        company: company,
        activity: activityInformation,
        hasSkippedActivityForm: hasSkippedActivityForm,
      };

      const onboardingResponse = await OnboardingService.onboardUserAndCreateCompany(onboardingForm);

      onboardingResponse.onSuccess(async (succesfullyOnboarding: SuccessfullyOnboarding) => {
        sessionStorage.removeItem("userOnboarding");
        sessionStorage.removeItem("companyInformation");
        sessionStorage.removeItem("activityOnboarding");
        sessionStorage.removeItem("hasSkippedActivityForm");

        // @ts-ignore
        await LazoLeadService.matchLeadToUser(user);

        if (!userComesFromProductPage) {
          window.dataLayer.push({
            email: user.email,
            userip: succesfullyOnboarding.userIp,
            firstname: succesfullyOnboarding.firstName,
            lastname: succesfullyOnboarding.lastName,
            event: succesfullyOnboarding.event,
          });
          console.log("[GA]", "Created account");
        }

        if (!user.isOAuthSignUp) {
          const loginResponse = await CustomAuthServiceV2.validateLoginWithEmailAndPassword(user.email, user.password);

          return loginResponse.onSuccess(async (publicAuthenticationToken) => {
            await Promise.all([
              AsyncAuthService.storeUser(publicAuthenticationToken.user),
              AsyncAuthService.storeToken(publicAuthenticationToken.token, publicAuthenticationToken.expiresAt),
            ]);

            redirect();
          });
        } else {
          const OAuthUser: OAuthRedirectUser = JSON.parse(sessionStorage.getItem("oauth_user"));
          const r = await LazoUserService.getByCoreUserExternalId(OAuthUser.externalId);
          r.onSuccess(async (lu) => {
            await Promise.all([AsyncAuthService.storeToken(sessionStorage.getItem("temp_token")), AsyncAuthService.storeUser(lu.user)]);
            sessionStorage.removeItem("temp_token");
            sessionStorage.removeItem("oauth_user");
            redirect();
          });
          r.onError(() => {
            NotificationService.error("There was an unexpected error. Try again");
          });
        }
      });

      onboardingResponse.onError((message) => {
        NotificationService.error(message);
        setIsLoading(false);
      });
    } catch (error) {
      console.error("Error during registration and login:", error);
      NotificationService.error(error.message || "An error occurred during registration.");
      setIsLoading(false);
    }
  };

  const redirect = async () => {
    const isUserLogged = await checkIfUserIsLogged();
    if (isMobile) {
      navigate(LazoPaths.NOT_SUPPORTED);
    } else {
      if (isUserLogged) {
        window.location.href = LazoPaths.DASHBOARD;
      } else {
        NotificationService.error("User is not logged in.");
        setIsLoading(false);
      }
    }
  };

  const checkIfUserIsLogged = async () => {
    const user = await AsyncAuthService.getUser();

    return new Promise((resolve) => {
      if (user) {
        resolve(true);
      } else {
        resolve(false);
      }
    });
  };

  const handleOnClickReady = async () => {
    const user: UserOnboarding = JSON.parse(sessionStorage.getItem("userOnboarding"));

    await registerUserAndAddToCompany(user);
  };

  return (
    <GenericForm
      noMargin
      disableAlert
      validateBeforeClosing
      resolver={yupResolver(schema) as unknown as Resolver<ActivityOnboarding>}
      buttonsContainerSxProps={{ display: "none" }}
      submitRef={submitRef}
      additionalButtonsBuilder={userComesFromProductPage ? () => <></> : () => <PreviousButton handlePrevious={handlePrevious} />}
      dialog={false}
      resetValue={{
        ...JSON.parse(sessionStorage.getItem("activityOnboarding") || "null"),
        statesWithEmployees: 0,
      }}
      onSave={(instance) => {
        if (userComesFromProductPage) {
          sessionStorage.setItem("activityOnboarding", JSON.stringify(instance as ActivityOnboarding));
          // sessionStorage.setItem("activityFromProductInfo", JSON.stringify(instance as ActivityOnboarding));
          handleContinue(instance as ActivityOnboarding, false);
          return;
        }

        sessionStorage.setItem("activityOnboarding", JSON.stringify(instance as ActivityOnboarding));

        handleContinue(instance as ActivityOnboarding, false);
      }}
      renderContent={(_, control, useFormHookInstance) => {
        const {
          formState: { errors },
          setValue,
          getValues,
        } = useFormHookInstance;
        setErrorsRef(errors);
        const companyType = useFormHookInstance.watch("companyType");
        const tangibleAssets = useFormHookInstance.watch("tangibleAssets");
        const foreignAccounts = useFormHookInstance.watch("foreignAccounts");
        const taxAssistance = useFormHookInstance.watch("taxAssistance");
        const internationalSubsidiaries = useFormHookInstance.watch("internationalSubsidiaries");
        const foreignShareholders = useFormHookInstance.watch("foreignShareholders");
        const lastYearCompanyType = useFormHookInstance.watch("lastYearCompanyType");
        const lastYearForeignAccounts = useFormHookInstance.watch("lastYearForeignAccounts");
        const lastYearForeignShareholders = useFormHookInstance.watch("lastYearForeignShareholders");
        const lastYearInternationalSubsidiaries = useFormHookInstance.watch("lastYearInternationalSubsidiaries");
        const lastYearDomesticSubsidiaries = useFormHookInstance.watch("lastYearDomesticSubsidiaries");

        const handleAddClick = () => {
          // @ts-ignore - I think we can remove parseInt but not sure so better not to mess with it
          const current = parseInt(getValues().statesWithEmployees);
          setValue("statesWithEmployees", current < 0 ? 0 : current + 1);
        };

        const handleRemoveClick = () => {
          // @ts-ignore - Idem
          const current = parseInt(getValues().statesWithEmployees);
          setValue("statesWithEmployees", current <= 0 ? 0 : current - 1);
        };

        return (
          <Container className="main-container">
            <Container className="polygons-container">
              <LazoPurpleBackground showLogo={true} fullwidth={false} />
            </Container>
            <Container className="background-container">
              <OboardingStepsComponent step={2} />
            </Container>
            <Container className="body">
              <Container className="form-container">
                <HeaderOnBoardingComponent step={2} />
                <Box
                  sx={{
                    display: "flex",
                    flexFlow: "column",
                    gap: ".75rem",
                    alignItems: "center",
                    width: "100%",
                    overflowY: "scroll",
                  }}
                >
                  <Text className="title">Activity</Text>
                  <Text className="activity-form-subtitle">Fill this section if interested in being quoted for Accounting services</Text>
                  <Container className="form-subcontainer">
                    <Container className="form-item" ref={myRefs.expenses}>
                      <Text className="gray-text">Estimated 2023 annual expenses</Text>
                      <TextFieldInput
                        InputProps={{
                          id: "qa-lazo-expenses",
                          startAdornment: <InputAdornment position={"start"}>USD</InputAdornment>,
                          inputProps: {
                            type: "number",
                            min: 0,
                          },
                        }}
                        propertyName="expenses"
                        control={control}
                        disabled={false}
                        TextFieldProps={{
                          sx: { width: "100%", ...textFieldStyles },
                        }}
                      />
                    </Container>
                    <Container className="form-item" ref={myRefs.statesWithEmployees}>
                      <Text className="gray-text">How many states does your company have W2 employees in</Text>
                      <TextFieldInput
                        InputProps={{
                          id: "qa-lazo-statesWithEmployees",
                          startAdornment: (
                            <InputAdornment position="start">
                              <IconButton
                                onClick={handleRemoveClick}
                                sx={{
                                  backgroundColor: Colors.BUTTON_SECONDARY_PURPLE,
                                }}
                              >
                                <RemoveIcon style={{ color: Colors.LAZO_PURPLE }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleAddClick}
                                sx={{
                                  backgroundColor: Colors.BUTTON_SECONDARY_PURPLE,
                                }}
                              >
                                <AddIcon style={{ color: Colors.LAZO_PURPLE }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                          inputProps: {
                            type: "number",
                            style: { textAlign: "center" },
                            min: 0,
                          },
                        }}
                        propertyName="statesWithEmployees"
                        control={control}
                        disabled={false}
                        TextFieldProps={{
                          sx: { width: "100%", ...textFieldStyles },
                        }}
                      />
                    </Container>
                    <Container className="form-item" ref={myRefs.bookkeeper}>
                      <Text className="gray-text">Who does the bookkeeping?</Text>
                      <TextFieldInput
                        InputProps={{
                          id: "qa-lazo-bookkeeper",
                        }}
                        propertyName="bookkeeper"
                        control={control}
                        disabled={false}
                        TextFieldProps={{
                          sx: { width: "100%", ...textFieldStyles },
                        }}
                      />
                    </Container>
                    <Container className="form-item">
                      <RadioInput
                        propertyName="companyType"
                        control={control}
                        options={TypeOfBusinessArray}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value}
                        mapToControllerValue={(selectedOption) => selectedOption?.value}
                        MuiRadioProps={radioProps}
                        RadioGroupProps={radioGroupProps}
                        FormControlProps={formControlProps}
                      />
                    </Container>

                    {companyType === "Operating" && (
                      <Container className="form-item">
                        <OnboardingCheckbox
                          label={<Text className="checkbox-text">Tangible Assets</Text>}
                          propertyName="tangibleAssets"
                          control={control}
                        />
                      </Container>
                    )}

                    {companyType === "Operating" && tangibleAssets && (
                      <Container className="form-item" ref={myRefs.tangibleAssetsAmount}>
                        <Text className="gray-text">How many of them?</Text>
                        <TextFieldInput
                          InputProps={{
                            id: "qa-lazo-tangibleAssetsAmount",
                          }}
                          propertyName="tangibleAssetsAmount"
                          control={control}
                          disabled={false}
                          TextFieldProps={{
                            sx: { width: "100%", ...textFieldStyles },
                          }}
                        />
                      </Container>
                    )}

                    <Container className="form-item" ref={myRefs.foreignAccounts}>
                      <OnboardingCheckbox
                        label={<Text className="checkbox-text">I have foreign accounts with more than $10,000</Text>}
                        propertyName="foreignAccounts"
                        control={control}
                      />
                    </Container>

                    {foreignAccounts && (
                      <Container className="form-item">
                        <Text className="gray-text">How many of them?</Text>
                        <TextFieldInput
                          InputProps={{
                            id: "qa-lazo-foreignAccountsAmount",
                          }}
                          propertyName="foreignAccountsAmount"
                          control={control}
                          disabled={false}
                          TextFieldProps={{
                            sx: { width: "100%", ...textFieldStyles },
                          }}
                        />
                      </Container>
                    )}

                    <Container className="form-item" ref={myRefs.foreignShareholders}>
                      <OnboardingCheckbox
                        id="qa-lazo-foreignShareholders"
                        label={<Text className="checkbox-text">I have foreign shareholders that own more than 25% with in 2023</Text>}
                        propertyName="foreignShareholders"
                        control={control}
                      />
                    </Container>

                    {foreignShareholders && (
                      <Container className="form-item" ref={myRefs.foreignShareholdersAmount}>
                        <Text className="gray-text">How many of them?</Text>
                        <TextFieldInput
                          InputProps={{
                            id: "qa-lazo-foreignShareholdersAmount",
                          }}
                          propertyName="foreignShareholdersAmount"
                          control={control}
                          disabled={false}
                          TextFieldProps={{
                            sx: { width: "100%", ...textFieldStyles },
                          }}
                        />
                      </Container>
                    )}

                    <Container className="form-item" ref={myRefs.foreignShareholders}>
                      <OnboardingCheckbox
                        id="qa-lazo-completeBookkeeping"
                        label={
                          <Text className="checkbox-text">
                            I would like Lazo to complete my 2023 bookkeeping and prepare these Financial Statements for taxes
                          </Text>
                        }
                        propertyName="completeBookkeeping"
                        control={control}
                      />
                    </Container>

                    <Container className="form-item">
                      <OnboardingCheckbox
                        id="qa-lazo-monthlyBookkeeping"
                        label={<Text className="checkbox-text">I am interested in monthly bookkeeping</Text>}
                        propertyName="monthlyBookkeeping"
                        control={control}
                      />
                    </Container>

                    <Container className="form-item" ref={myRefs.internationalSubsidiaries}>
                      <OnboardingCheckbox
                        id="qa-lazo-internationalSubsidiaries"
                        label={<Text className="checkbox-text">I have international subsidiaries in 2023</Text>}
                        propertyName="internationalSubsidiaries"
                        control={control}
                      />
                    </Container>

                    {internationalSubsidiaries && (
                      <Container className="form-item" ref={myRefs.internationalSubsidiariesAmount}>
                        <Text className="gray-text">How many of them?</Text>
                        <TextFieldInput
                          InputProps={{
                            id: "qa-lazo-internationalSubsidiariesAmount",
                          }}
                          propertyName="internationalSubsidiariesAmount"
                          control={control}
                          disabled={false}
                          TextFieldProps={{
                            sx: { width: "100%", ...textFieldStyles },
                          }}
                        />
                      </Container>
                    )}

                    <Container className="form-item" ref={myRefs.domesticSubsidiaries}>
                      <OnboardingCheckbox
                        id="qa-lazo-domesticSubsidiaries"
                        label={<Text className="checkbox-text">I have domestic subsidiaries in 2023</Text>}
                        propertyName="domesticSubsidiaries"
                        control={control}
                      />
                    </Container>

                    <Container className="form-item" ref={myRefs.taxAssistance}>
                      <OnboardingCheckbox
                        id="qa-lazo-taxAssistance"
                        label={<Text className="checkbox-text">I need assistance with my Tax Year return 2022</Text>}
                        propertyName="taxAssistance"
                        control={control}
                      />
                    </Container>
                    {taxAssistance && (
                      <>
                        <Container className="form-item" ref={myRefs.lastYearExpenses}>
                          <Text className="gray-text">Total estimated 2022 expenses</Text>
                          <TextFieldInput
                            InputProps={{
                              id: "qa-lazo-lastYearExpenses",
                            }}
                            propertyName="lastYearExpenses"
                            control={control}
                            disabled={false}
                            TextFieldProps={{
                              sx: { width: "100%", ...textFieldStyles },
                            }}
                          />
                        </Container>

                        <Container className="form-item">
                          <RadioInput
                            propertyName="lastYearCompanyType"
                            control={control}
                            options={TypeOfBusinessArray}
                            isOptionEqualToValue={(option, value) => option.value === value}
                            getOptionLabel={(option) => option.label}
                            MuiRadioProps={radioProps}
                            mapToControllerValue={(selectedOption) => selectedOption?.value}
                            FormControlProps={formControlProps}
                            RadioGroupProps={radioGroupProps}
                          />
                        </Container>

                        {lastYearCompanyType === "Holding" && (
                          <Container className="form-item">
                            <OnboardingCheckbox
                              id="qa-lazo-lastYearTangibleAssets"
                              label={<Text className="checkbox-text">Tangible Assets in 2022</Text>}
                              propertyName="lastYearTangibleAssets"
                              control={control}
                            />
                          </Container>
                        )}

                        <Container className="form-item" ref={myRefs.lastYearStatesWithEmployees}>
                          <Text className="gray-text">How many states does your company have employess in 2022</Text>
                          <TextFieldInput
                            InputProps={{
                              id: "qa-lazo-lastYearStatesWithEmployees",
                            }}
                            propertyName="lastYearStatesWithEmployees"
                            control={control}
                            disabled={false}
                            TextFieldProps={{
                              sx: { width: "100%", ...textFieldStyles },
                            }}
                          />
                        </Container>

                        <Container className="form-item" ref={myRefs.lastYearForeignAccounts}>
                          <OnboardingCheckbox
                            id="qa-lazo-lastYearForeignAccounts"
                            label={<Text className="checkbox-text">Do you have foreign bank accounts with $10,000 during 2022?</Text>}
                            propertyName="lastYearForeignAccounts"
                            control={control}
                          />
                        </Container>

                        {lastYearForeignAccounts && (
                          <Container className="form-item">
                            <Text className="gray-text">How many of them?</Text>
                            <TextFieldInput
                              InputProps={{
                                id: "qa-lazo-lastYearForeignAccountsAmount",
                              }}
                              propertyName="lastYearForeignAccountsAmount"
                              control={control}
                              disabled={false}
                              TextFieldProps={{
                                sx: { width: "100%", ...textFieldStyles },
                              }}
                            />
                          </Container>
                        )}

                        <Container className="form-item" ref={myRefs.lastYearForeignShareholders}>
                          <OnboardingCheckbox
                            id="qa-lazo-lastYearForeignShareholders"
                            label={<Text className="checkbox-text">Do you have foreing Shareholders that own more than 25% in 2022?</Text>}
                            propertyName="lastYearForeignShareholders"
                            control={control}
                          />
                        </Container>

                        {lastYearForeignShareholders && (
                          <Container className="form-item">
                            <Text className="gray-text">How many of them?</Text>
                            <TextFieldInput
                              InputProps={{
                                id: "qa-lazo-lastYearForeignShareholdersAmount",
                              }}
                              propertyName="lastYearForeignShareholdersAmount"
                              control={control}
                              disabled={false}
                              TextFieldProps={{
                                sx: { width: "100%", ...textFieldStyles },
                              }}
                            />
                          </Container>
                        )}

                        <Container className="form-item" ref={myRefs.completeBookkeepingLastYear}>
                          <OnboardingCheckbox
                            id="qa-lazo-completeBookkeepingLastYear"
                            label={<Text className="checkbox-text">Would you like Lazo to complete your bookkeeping for 2022?</Text>}
                            propertyName="completeBookkeepingLastYear"
                            control={control}
                          />
                        </Container>

                        <Container className="form-item" ref={myRefs.lastYearInternationalSubsidiaries}>
                          <OnboardingCheckbox
                            id="qa-lazo-lastYearInternationalSubsidiaries"
                            label={<Text className="checkbox-text">Do you have international subsidiaries in 2022?</Text>}
                            propertyName="lastYearInternationalSubsidiaries"
                            control={control}
                          />
                        </Container>

                        {lastYearInternationalSubsidiaries && (
                          <Container className="form-item">
                            <Text className="gray-text">How many of them?</Text>
                            <TextFieldInput
                              InputProps={{
                                id: "qa-lazo-lastYearInternationalSubsidiariesAmount",
                              }}
                              propertyName="lastYearInternationalSubsidiariesAmount"
                              control={control}
                              disabled={false}
                              TextFieldProps={{
                                sx: { width: "100%", ...textFieldStyles },
                              }}
                            />
                          </Container>
                        )}

                        <Container className="form-item" ref={myRefs.lastYearDomesticSubsidiaries}>
                          <OnboardingCheckbox
                            id="qa-lazo-lastYearDomesticSubsidiaries"
                            label={<Text className="checkbox-text">Do you have domestic subsidiaries in 2022?</Text>}
                            propertyName="lastYearDomesticSubsidiaries"
                            control={control}
                          />
                        </Container>

                        {lastYearDomesticSubsidiaries && (
                          <Container className="form-item">
                            <Text className="gray-text">How many of them?</Text>
                            <TextFieldInput
                              InputProps={{
                                id: "qa-lazo-lastYearDomesticSubsidiariesAmount",
                              }}
                              propertyName="lastYearDomesticSubsidiariesAmount"
                              control={control}
                              disabled={false}
                              TextFieldProps={{
                                sx: { width: "100%", ...textFieldStyles },
                              }}
                            />
                          </Container>
                        )}
                      </>
                    )}
                  </Container>
                  <Container
                    sx={{
                      width: !isMobile ? "70%" : "100%",
                      [theme.breakpoints.down("sm")]: {
                        width: "100%",
                        justifyContent: "center",
                        gap: 1,
                      },
                      justifyContent: "space-between",
                      padding: "1rem 0",
                      alignItems: "center",
                    }}
                  >
                    {!userComesFromProductPage && !isLoggedUser && !isMobile && (
                      <Container sx={{ width: "30%" }}>
                        <PreviousButton handlePrevious={handlePrevious} />
                      </Container>
                    )}
                    <Container
                      sx={{
                        width: "60%",
                        gap: 1,
                        justifyContent: "flex-end",
                        [theme.breakpoints.down("sm")]: {
                          width: "100%",
                        },
                      }}
                    >
                      {isMobile && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            height: "100%",
                            alignItems: "center",
                            justifyContent: "space-between",
                            zIndex: 0,
                            position: "relative",
                          }}
                        >
                          <StyledImage
                            className="sign-up-info"
                            src={SignUpQuestionLogo.src}
                            style={{
                              position: "absolute",
                              top: 0,
                              right: 20,
                              zIndex: 2,
                            }}
                          />
                          <Divider
                            sx={{
                              width: "100%",
                              marginBottom: "1rem",
                              marginTop: isMobile ? 0 : "4.2rem",
                            }}
                          />

                          {
                            <>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  width: "100%",
                                  padding: "12px, 16px, 12px, 16px",
                                  alignItems: "center",
                                  flexDirection: "row",
                                }}
                              >
                                {!userComesFromProductPage && (
                                  <SkipForNowButton
                                    onClick={() => {
                                      sessionStorage.setItem("hasSkippedActivityForm", "false");
                                      handleOnClickReady();
                                    }}
                                  >
                                    <Text className="button-text">Skip for now</Text>
                                  </SkipForNowButton>
                                )}

                                <ContinueButton
                                  hideIcon
                                  buttonColor={Colors.LAZO_PURPLE}
                                  text="Continue"
                                  handleContinue={() => handleFormSubmit()}
                                  minWidth={"0rem"}
                                />
                              </Box>
                              {!userComesFromProductPage && (
                                <Container sx={{ width: "30%" }}>
                                  <PreviousButton handlePrevious={handlePrevious} />
                                </Container>
                              )}
                            </>
                          }
                        </div>
                      )}

                      {!userComesFromProductPage && !isLoggedUser && !isMobile && (
                        <SkipForNowButton
                          disabled={isLoading}
                          onClick={() => {
                            sessionStorage.setItem("hasSkippedActivityForm", "true");
                            handleOnClickReady();
                          }}
                        >
                          {isLoading ? (
                            <Spinner variant={"indeterminate"} sx={{ color: Colors.LAZO_PURPLE }} />
                          ) : (
                            <Text className="button-text"> Skip for now</Text>
                          )}
                        </SkipForNowButton>
                      )}

                      {!isMobile && (
                        <ContinueButton
                          hideIcon
                          buttonColor={Colors.LAZO_PURPLE}
                          text="Continue"
                          handleContinue={() => handleFormSubmit()}
                          minWidth={"0rem"}
                        />
                      )}
                    </Container>
                  </Container>
                </Box>
              </Container>
            </Container>
            {!isMobile && <StyledImage className="sign-up-info" src={SignUpQuestionLogo.src} />}
          </Container>
        );
      }}
    />
  );
};
